/**
 * Leitura do gate de versão mínima suportada (PRD §10.15).
 *
 * A tabela `app_settings` expõe publicamente apenas as chaves `min_supported_build.*` — o gate
 * precisa responder antes de qualquer login. Ver a migration `20260831134929_app_settings.sql`.
 */

export type GatePlatform = 'ios' | 'android' | 'web';

export interface MinSupportedBuild {
  build: number;
  /** Chave i18n da mensagem do ecrã bloqueante. A chave `web` não a define. */
  messageKey: string | null;
}

/** O gate roda a cada foreground; sem cache, cada alternância de app custaria uma chamada. */
export const MIN_SUPPORTED_BUILD_CACHE_TTL_MS = 60 * 60 * 1000;

export function minSupportedBuildKey(platform: GatePlatform): string {
  return `min_supported_build.${platform}`;
}

/**
 * Forma mínima que esta leitura exige do client Supabase. Declará-la explicitamente mantém o
 * módulo testável sem subir um client real e sem acoplar à superfície inteira do SDK.
 *
 * Os tipos do `postgrest-js` são genéricos e profundos: exigir a assinatura exata faria o
 * `SupabaseClient` real não encaixar aqui (TS2345) e levaria o compilador a instanciações
 * infinitas (TS2589). O contrato que importa é o encadeamento, não os genéricos.
 */
export interface AppSettingsReader {
  from(table: string): {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    select(columns: string): any;
  };
}

interface CacheEntry {
  value: MinSupportedBuild;
  expiresAt: number;
}

const cache = new Map<GatePlatform, CacheEntry>();

/** Uso exclusivo dos testes: zera o cache entre casos. */
export function __resetMinSupportedBuildCache(): void {
  cache.clear();
}

function parseSetting(raw: unknown): MinSupportedBuild | null {
  if (typeof raw !== 'object' || raw === null) return null;

  const value = (raw as { value?: unknown }).value;
  if (typeof value !== 'object' || value === null) return null;

  const build = (value as { build?: unknown }).build;
  if (typeof build !== 'number' || !Number.isFinite(build)) return null;

  const messageKey = (value as { message_key?: unknown }).message_key;

  return {
    build,
    messageKey: typeof messageKey === 'string' ? messageKey : null,
  };
}

/**
 * Devolve o build mínimo suportado, ou `null` quando não for possível determiná-lo.
 *
 * `null` é deliberadamente indistinguível entre "rede fora", "chave ausente" e "valor inválido":
 * em todos esses casos o chamador deve **falhar aberto**. Um gate que trava o app quando a
 * leitura falha transforma indisponibilidade do backend em indisponibilidade total do produto.
 */
export async function readMinSupportedBuild(
  client: AppSettingsReader,
  platform: GatePlatform,
): Promise<MinSupportedBuild | null> {
  const cached = cache.get(platform);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.value;
  }

  // O resultado é reduzido ao contrato mínimo assim que sai do SDK; `parseSetting` valida a
  // forma em runtime, que é o que realmente protege — o schema é externo ao TypeScript.
  //
  // Reavaliado na T0.8, quando o client passou a ser tipado por `Database`: o cast permanece de
  // propósito. `value` é `Json` no tipo gerado, logo continuaria a exigir estreitamento; e trocar
  // `AppSettingsReader` pelo `SupabaseClient` concreto obrigaria o teste a subir um client real,
  // perdendo os 10 cenários que hoje correm sem rede.
  const { data, error } = (await client
    .from('app_settings')
    .select('value')
    .eq('key', minSupportedBuildKey(platform))
    .maybeSingle()) as { data: unknown; error: { message: string } | null };

  if (error) return null;

  const parsed = parseSetting(data);
  // Falhas não são cacheadas: cachear manteria o gate cego por 1 h após uma oscilação de rede.
  if (!parsed) return null;

  cache.set(platform, {
    value: parsed,
    expiresAt: Date.now() + MIN_SUPPORTED_BUILD_CACHE_TTL_MS,
  });

  return parsed;
}
